import { Alert, Divider, Group, Stack, Text, TextInput } from '@mantine/core'
import { Button, cn, H3 } from '@packages/ui'
import { useTranslation } from 'react-i18next'
import { ForgotPasswordForm } from './ForgotPasswordForm'
import { useAuthSignIn } from './hooks/useAuthSignIn'
import { useForgotPassword } from './hooks/useForgotPassword'
import { renderAuthSlot } from './renderSlot'
import type { AuthSignInFormProps, AuthSignInContext } from './types'

function SignInForgotPasswordView({
  ctx,
  slots
}: {
  ctx: AuthSignInContext
  slots?: AuthSignInFormProps['slots']
}) {
  const forgot = useForgotPassword({
    email: ctx.state.email,
    setEmail: ctx.actions.setEmail,
    redirectTo: ctx.state.redirectTo,
    strategy: 'supabase'
  })

  if (slots?.forgotPassword) {
    return <>{renderAuthSlot(slots.forgotPassword, () => null, ctx)}</>
  }

  return (
    <ForgotPasswordForm
      forgot={forgot}
      onBackToSignIn={() => {
        ctx.actions.setView('sign_in')
        ctx.actions.clearError()
        forgot.actions.resetSentState()
      }}
    />
  )
}

function SignInFormBody({
  ctx,
  variant,
  slots,
  classNames,
  onNavigateToSignup,
  signupPrompt
}: Omit<AuthSignInFormProps, 'auth' | 'authOptions'> & {
  ctx: AuthSignInContext
}) {
  const { t } = useTranslation()
  const { state, actions } = ctx
  const {
    email,
    password,
    loading,
    error,
    resultMessage,
    view,
    showPasswordLogin
  } = state

  if (view === 'forgot_password') {
    const forgotView = <SignInForgotPasswordView ctx={ctx} slots={slots} />
    return slots?.panel ? <>{slots.panel(forgotView)}</> : forgotView
  }

  const signIn = (
    <Stack className={classNames?.panel}>
      {view === 'sign_in' &&
        renderAuthSlot(
          slots?.heading,
          () => (
            <H3 data-testid="signin-heading" className={classNames?.heading}>
              {t('auth.sign_in.label', { defaultValue: 'Login' })}
            </H3>
          ),
          ctx
        )}

      {renderAuthSlot(
        slots?.providers,
        () => (
          <Stack>
            {variant === 'staff' && !showPasswordLogin ? (
              <>
                {renderAuthSlot(slots?.passkey, () => null, ctx)}
                {renderAuthSlot(
                  slots?.google,
                  () => (
                    <Button
                      onClick={actions.signInWithGoogle}
                      data-testid="google-button"
                      size="md"
                    >
                      {t('auth.continue_with_google.label', {
                        defaultValue: 'With Google'
                      })}
                    </Button>
                  ),
                  ctx
                )}
                {renderAuthSlot(
                  slots?.passwordToggle,
                  () => (
                    <Button
                      size="md"
                      variant="default"
                      onClick={() => actions.setShowPasswordLogin(true)}
                      data-testid="login-with-password-button"
                    >
                      {t('auth.continue_with_password.label', {
                        defaultValue: 'With Email & Password'
                      })}
                    </Button>
                  ),
                  ctx
                )}
              </>
            ) : (
              <>
                {renderAuthSlot(slots?.passkey, () => null, ctx)}
                {renderAuthSlot(
                  slots?.google,
                  () => (
                    <Button
                      onClick={actions.signInWithGoogle}
                      data-testid="google-button"
                    >
                      {t('auth.continue_with_google.label', {
                        defaultValue: 'Continue with Google'
                      })}
                    </Button>
                  ),
                  ctx
                )}
                {variant === 'default' && (
                  <Divider
                    label={t('auth.or.label', { defaultValue: 'or' })}
                    labelPosition="center"
                    my="md"
                  />
                )}
              </>
            )}
          </Stack>
        ),
        ctx
      )}

      {renderAuthSlot(
        slots?.error,
        () =>
          error ? (
            <Alert
              variant="default"
              data-testid="error-message"
              className={cn('text-red', classNames?.error)}
            >
              <Text className="text-red-600">
                {actions.translateError(error)}
              </Text>
            </Alert>
          ) : null,
        ctx
      )}

      {(variant === 'default' || showPasswordLogin) &&
        renderAuthSlot(
          slots?.passwordForm,
          () => (
            <form
              onSubmit={(e) => {
                e.preventDefault()
                void actions.signInWithPassword()
              }}
            >
              <Stack
                className={variant === 'staff' ? 'animate-appear' : undefined}
              >
                <TextInput
                  data-testid="email-input"
                  label={t('auth.email.label', { defaultValue: 'Email' })}
                  placeholder={t('auth.email.placeholder', {
                    defaultValue: 'email@example.com'
                  })}
                  value={email}
                  onChange={(e) => actions.setEmail(e.currentTarget.value)}
                />
                <TextInput
                  data-testid="password-input"
                  label={t('auth.password.input.label', {
                    defaultValue: 'Password'
                  })}
                  placeholder={t('auth.password.placeholder', {
                    defaultValue: '••••••••'
                  })}
                  type="password"
                  value={password}
                  onChange={(e) => actions.setPassword(e.currentTarget.value)}
                />
                <Group justify="flex-end">
                  {variant === 'staff' ? (
                    <>
                      <Button
                        size="xs"
                        variant="transparent"
                        onClick={() => actions.setView('forgot_password')}
                      >
                        {t('auth.forgot_password.label', {
                          defaultValue: 'Forgot Password'
                        })}
                      </Button>
                      <Button
                        variant="transparent"
                        ml="auto"
                        size="xs"
                        onClick={() => {
                          actions.clearError()
                          actions.setShowPasswordLogin(false)
                        }}
                      >
                        {t('cancel', { defaultValue: 'Cancel' })}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="xs"
                      variant="transparent"
                      onClick={() => actions.setView('forgot_password')}
                    >
                      {t('auth.forgot_password.label', {
                        defaultValue: 'Forgot Password'
                      })}
                    </Button>
                  )}
                  <Button
                    size="md"
                    type="submit"
                    loading={loading}
                    data-testid="signin-button"
                  >
                    {t('auth.sign_in.label', { defaultValue: 'Sign In' })}
                  </Button>
                </Group>
              </Stack>
            </form>
          ),
          ctx
        )}

      {signupPrompt &&
        renderAuthSlot(
          slots?.footer,
          () => (
            <Stack justify="center" mt="md">
              <H3>
                {t('auth.no_account.label', {
                  defaultValue: 'Sign Up'
                })}
              </H3>
              <Button
                variant="primary"
                size="lg"
                w="100%"
                onClick={onNavigateToSignup}
                className="font-medium"
              >
                {t('auth.sign_up.label', {
                  defaultValue: 'Try for free for 30 days'
                })}
              </Button>
            </Stack>
          ),
          ctx
        )}
    </Stack>
  )

  return slots?.panel ? <>{slots.panel(signIn)}</> : signIn
}

export const SignInForm = ({
  auth: authProp,
  authOptions,
  variant = 'default',
  slots,
  classNames,
  onNavigateToSignup,
  signupPrompt = false
}: AuthSignInFormProps) => {
  const internalAuth = useAuthSignIn(authOptions ?? {})
  const ctx = authProp ?? internalAuth

  return (
    <SignInFormBody
      ctx={ctx}
      variant={variant}
      slots={slots}
      classNames={classNames}
      onNavigateToSignup={onNavigateToSignup}
      signupPrompt={signupPrompt}
    />
  )
}
